import { useEffect, useMemo, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../firebase';
import SortableTable from '../components/SortableTable';
import useAdminAction from '../hooks/useAdminAction';
import useAdminRole from '../hooks/useAdminRole';
import { ADMIN_ACTIONS } from '../constants/adminActions';
import { adminRef, fetchAdmins } from '../services/firestore';

export default function AdminUsers() {
  const { execute, loading, error } = useAdminAction();
  const { isSuperAdmin } = useAdminRole();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [tableLoading, setTableLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [note, setNote] = useState('');
  const [removeEmail, setRemoveEmail] = useState('');

  const load = async () => {
    setTableLoading(true);
    try {
      setRows(await fetchAdmins());
    } finally {
      setTableLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const columns = useMemo(() => [
    { key: 'email', header: 'Email', accessor: (row) => row.id, render: (row) => row.id },
    { key: 'role', header: 'Role', accessor: 'role', render: (row) => row.role || '-' },
    { key: 'addedBy', header: 'Added By', accessor: 'addedBy', render: (row) => row.addedBy || '-' },
    {
      key: 'addedAt',
      header: 'Added At',
      accessor: 'addedAt',
      render: (row) => (row.addedAt?.toDate ? row.addedAt.toDate().toLocaleString() : '-'),
    },
    {
      key: 'actions',
      header: '',
      accessor: 'id',
      sortable: false,
      render: (row) => (
        isSuperAdmin && row.id !== (auth.currentUser?.email || '').toLowerCase() ? (
          <button type="button" className="danger" onClick={() => setRemoveEmail(row.id)}>Remove</button>
        ) : null
      ),
    },
  ], [isSuperAdmin]);

  const addAdmin = async () => {
    if (!isSuperAdmin || !note.trim()) return;
    const id = email.trim().toLowerCase();
    if (!id) return;

    await execute({
      action: ADMIN_ACTIONS.ADMIN_ADDED,
      targetCollection: 'admins',
      targetId: id,
      before: null,
      after: { role, email: id },
      note,
      applyChange: (batch, batchDb) => {
        batch.set(adminRef(batchDb, id), {
          role,
          name: id,
          addedBy: auth.currentUser?.email || 'unknown',
          addedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        }, { merge: true });
      },
    });

    setEmail('');
    setNote('');
    await load();
  };

  const removeAdmin = async () => {
    if (!isSuperAdmin || !removeEmail || !note.trim()) return;
    if (removeEmail === (auth.currentUser?.email || '').toLowerCase()) return;

    await execute({
      action: ADMIN_ACTIONS.ADMIN_REMOVED,
      targetCollection: 'admins',
      targetId: removeEmail,
      before: true,
      after: false,
      note,
      applyChange: (batch, batchDb) => {
        batch.delete(adminRef(batchDb, removeEmail));
      },
    });

    setRemoveEmail('');
    setNote('');
    await load();
  };

  return (
    <>
      <div className="wb-card">
        <h3 className="wb-section-title">Admin Users</h3>
        {!isSuperAdmin && <p className="wb-muted">Read-only: only super_admin can add/remove admins.</p>}
        <input
          className="wb-filter-input"
          placeholder="Filter admins"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          style={{ marginBottom: 12 }}
        />
        {tableLoading ? <div className="wb-skeleton wb-skeleton--table" /> : (
          <SortableTable
            columns={columns}
            rows={rows}
            filter={q}
            emptyText="No admin users found."
            defaultSort={{ key: 'email', dir: 'asc' }}
          />
        )}
      </div>

      {isSuperAdmin && (
        <div className="wb-card">
          <h3 className="wb-section-title">Manage Admin Access</h3>
          <div className="wb-row">
            <input placeholder="admin@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            <select value={role} onChange={(e) => setRole(e.target.value)}><option value="admin">admin</option><option value="super_admin">super_admin</option></select>
          </div>
          <textarea rows={3} placeholder="Required note" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="wb-row">
            <button disabled={loading} onClick={addAdmin}>Add Admin</button>
            <button className="danger" disabled={loading || !removeEmail} onClick={removeAdmin}>Confirm Remove: {removeEmail || '-'}</button>
          </div>
          {error && <p style={{ color: '#c62828' }}>{error}</p>}
        </div>
      )}
    </>
  );
}
